<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12">
        <h1>My Account</h1>
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <v-col cols="12" md="8">
        <v-card>
          <v-card-item>
            <template #title>Profile</template>
          </v-card-item>
          <v-card-text>
            <p class="text-body2"><strong>Name:</strong> {{ authStore.currentUser?.displayName || '—' }}</p>
            <p class="text-body2"><strong>Email:</strong> {{ authStore.currentUser?.email }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" closable @click:close="error = null" class="mb-4">
      {{ error }}
    </v-alert>

    <v-row v-if="isLoading">
      <v-col cols="12" md="8"><v-skeleton-loader type="card" /></v-col>
    </v-row>

    <v-row v-else-if="organizations.length === 0">
      <v-col cols="12" md="8">
        <v-card>
          <v-card-text class="text-medium-emphasis">
            You're not a member of any organization yet.
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else v-for="org in organizations" :key="org.id" class="mb-2">
      <v-col cols="12" md="8">
        <v-card>
          <v-card-item>
            <template #title>My Calendar Feed — {{ org.name }}</template>
            <template #subtitle>
              Subscribe to this link from Google Calendar, Apple Calendar, or Outlook to see this
              org's whole schedule. It's yours alone — regenerating it invalidates the old link.
            </template>
          </v-card-item>
          <v-card-text>
            <p class="text-caption text-medium-emphasis mb-3">
              Calendar apps refresh a subscribed link on their own schedule — sometimes not for
              several hours — so it can lag behind what's actually in Gigdriver. Gigdriver is always
              the source of truth when the two disagree.
            </p>
            <v-text-field
              :model-value="feedUrls[org.id] ?? ''"
              label="Feed URL"
              :loading="loadingOrgId === org.id && !feedUrls[org.id]"
              readonly
              density="compact"
              hide-details
              class="mb-3"
            >
              <template #append-inner>
                <v-btn
                  v-if="feedUrls[org.id]"
                  icon="mdi-content-copy"
                  size="small"
                  variant="text"
                  @click="copyUrl(org.id)"
                />
              </template>
            </v-text-field>
            <div class="d-flex align-center">
              <v-btn
                v-if="!feedUrls[org.id]"
                size="small"
                variant="tonal"
                :loading="loadingOrgId === org.id"
                @click="loadFeedUrl(org.id)"
              >
                Get My Link
              </v-btn>
              <v-btn
                v-else
                size="small"
                variant="text"
                color="warning"
                :loading="loadingOrgId === org.id"
                @click="regenerate(org.id)"
              >
                Regenerate Link
              </v-btn>
              <span v-if="copiedOrgId === org.id" class="text-caption text-success ml-2">Copied!</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { getMyCalendarFeedUrl, regenerateMyCalendarFeedUrl } from '@gigdriver/client';
import { useAuthStore } from '../stores/auth';
import { useOrganizationStore } from '../stores/organization';

const authStore = useAuthStore();
const orgStore = useOrganizationStore();

const organizations = ref(orgStore.organizations);
const isLoading = ref(false);
const error = ref<string | null>(null);
const loadingOrgId = ref<string | null>(null);
const copiedOrgId = ref<string | null>(null);
const feedUrls = reactive<Record<string, string>>({});

onMounted(async () => {
  if (!authStore.currentUser) return;
  try {
    isLoading.value = true;
    await orgStore.loadUserOrganizations(authStore.currentUser.uid);
    organizations.value = orgStore.organizations;
  } finally {
    isLoading.value = false;
  }
  // getMyCalendarFeedUrl is idempotent server-side (mints a token once,
  // returns the same link on every later call) — load it eagerly for every
  // org so it's already visible/copyable on arrival, instead of requiring
  // an extra click every time this page is revisited to re-reveal a link
  // that already exists. Bypasses loadFeedUrl (and its loadingOrgId flag,
  // reserved for the explicit Get/Regenerate buttons) so N parallel loads
  // here don't fight over one shared loading indicator.
  await Promise.all(
    organizations.value.map(async org => {
      try {
        const { url } = await getMyCalendarFeedUrl({ orgId: org.id });
        feedUrls[org.id] = url;
      } catch {
        // Leave this org's card showing "Get My Link" as a manual fallback.
      }
    }),
  );
});

async function loadFeedUrl(orgId: string) {
  try {
    loadingOrgId.value = orgId;
    error.value = null;
    const { url } = await getMyCalendarFeedUrl({ orgId });
    feedUrls[orgId] = url;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load your calendar feed link';
  } finally {
    loadingOrgId.value = null;
  }
}

async function regenerate(orgId: string) {
  if (!confirm('Regenerate this link? The old one will stop working immediately.')) return;
  try {
    loadingOrgId.value = orgId;
    error.value = null;
    const { url } = await regenerateMyCalendarFeedUrl({ orgId });
    feedUrls[orgId] = url;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to regenerate your calendar feed link';
  } finally {
    loadingOrgId.value = null;
  }
}

async function copyUrl(orgId: string) {
  const url = feedUrls[orgId];
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    copiedOrgId.value = orgId;
    setTimeout(() => {
      if (copiedOrgId.value === orgId) copiedOrgId.value = null;
    }, 2000);
  } catch {
    // Clipboard access can be denied by the browser — the field is still
    // selectable/readable manually, so this is a soft failure.
  }
}
</script>
